/**
 * Multi-select for master-data job roles (not RBAC roles).
 * Popover + checkboxes; selected roles shown as removable chips.
 */
import React from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatMasterOptionLabel } from '@/lib/masterDataLabels';

export default function RoleMultiSelect({
  roles = [],
  value = [],
  onChange,
  placeholder = 'Select roles',
  disabled = false,
  activeOnly = true,
  testId = 'role-multi-select',
  className,
}) {
  const [open, setOpen] = React.useState(false);
  const selected = Array.isArray(value) ? value : [];
  const options = (roles || []).filter((r) => !activeOnly || r.status === 'active');
  const byId = React.useMemo(() => {
    const m = new Map();
    for (const r of options) m.set(r.id, r);
    // Also keep labels for selected inactive roles still in value
    for (const r of roles || []) {
      if (!m.has(r.id)) m.set(r.id, r);
    }
    return m;
  }, [options, roles]);

  const toggle = (id) => {
    if (disabled) return;
    if (selected.includes(id)) {
      onChange?.(selected.filter((x) => x !== id));
    } else {
      onChange?.([...selected, id]);
    }
  };

  const remove = (id, e) => {
    e?.stopPropagation?.();
    if (disabled) return;
    onChange?.(selected.filter((x) => x !== id));
  };

  const label =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? (formatMasterOptionLabel(byId.get(selected[0]) || {}) || '1 role')
        : `${selected.length} roles selected`;

  return (
    <div className={cn('space-y-1.5', className)} data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="h-9 w-full justify-between font-normal"
            data-testid={`${testId}-trigger`}
          >
            <span className={cn('truncate', selected.length === 0 && 'text-muted-foreground')}>
              {label}
            </span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <div className="max-h-56 overflow-y-auto p-1" data-testid={`${testId}-list`}>
            {options.length === 0 ? (
              <p className="px-2 py-3 text-xs text-muted-foreground">No roles available</p>
            ) : (
              options.map((r) => {
                const checked = selected.includes(r.id);
                return (
                  <div
                    key={r.id}
                    role="option"
                    aria-selected={checked}
                    aria-label={formatMasterOptionLabel(r)}
                    tabIndex={disabled ? -1 : 0}
                    className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    onClick={() => toggle(r.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle(r.id);
                      }
                    }}
                    data-testid={`${testId}-option-${r.id}`}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border border-primary shadow',
                        checked
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background',
                      )}
                    >
                      {checked ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                    </span>
                    <span className="flex-1 truncate">{formatMasterOptionLabel(r)}</span>
                  </div>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`${testId}-chips`}>
          {selected.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-md border bg-secondary/50 px-1.5 py-0.5 text-[11px]"
            >
              {formatMasterOptionLabel(byId.get(id) || { code: id.slice(0, 8) })}
              <button
                type="button"
                className="rounded p-0.5 hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
                disabled={disabled}
                onClick={(e) => remove(id, e)}
                aria-label={`Remove ${formatMasterOptionLabel(byId.get(id) || {})}`}
                data-testid={`${testId}-remove-${id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
